/**
 * Collectors adicionales de un usuario (`users.multi_iid`) — lambda `user_access`
 * de webadmin2. Es API de webadmin2 → JWT post-MFA (`webadmin2.jwt`), como
 * collectorStats.ts. El alta del usuario sigue yendo por CoreDB (collectors.ts).
 *
 * Los ids son VENDOR iids (`collection_config` PRODUCT#<id>/vendor), no
 * collector_id. La normalización ("56,78", sin espacios, con el principal) la
 * hace el backend; acá solo se manda la lista elegida.
 */
const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? "http://localhost:3000";

export interface UserAccess {
  uid:        string;          // "<email>#vendorweb"
  email:      string;
  iid:        number | null;   // vendor principal
  multi_iid:  string | null;   // valor crudo guardado (para el control de concurrencia)
  collectors: number[];        // vendor iids, ascendente, incluye el principal
}

export interface CollectorAccess {
  collector_id: string;
  vendor_iid:   number;
  users:        UserAccess[];
  /** vendor iid → collector_ids (solo los que existen; un iid ausente = huérfano). */
  collectors:   Record<string, string[]>;
}

function authHeader(): Record<string, string> {
  const jwt = localStorage.getItem("webadmin2.jwt");
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
}

async function authFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const res = await fetch(url, init);
  if (res.status === 401) {
    localStorage.removeItem("webadmin2.jwt");
    localStorage.removeItem("webadmin2.email");
    if (typeof window !== "undefined") window.location.replace("/login");
  }
  return res;
}

export async function getCollectorAccess(collectorId: string): Promise<CollectorAccess> {
  const res = await authFetch(`${API_BASE}/user-access?${new URLSearchParams({ collector_id: collectorId })}`, { headers: authHeader() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? `getCollectorAccess: ${res.status}`);
  return data as CollectorAccess;
}

/** Reemplaza la lista de collectors del usuario. `expected` = lo que se leyó (409 si cambió). */
export async function setUserCollectors(
  email: string, collectors: number[], expected: { iid: number | null; multi_iid: string | null },
): Promise<UserAccess & { changed: boolean }> {
  const res = await authFetch(`${API_BASE}/user-access`, {
    method: "PUT", headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify({ email, collectors, expected }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? `setUserCollectors: ${res.status}`);
  return data;
}

/** Umbral de CoreDB: bloqueado si failed_login_attempts > 3 (AuthManager). */
export const LOCK_THRESHOLD = 3;
export const isLocked = (failedAttempts: number) => failedAttempts > LOCK_THRESHOLD;

/** Desbloquea (mismo efecto que el link del mail de CoreDB). 409 si no estaba bloqueado. */
export async function unlockUser(email: string): Promise<void> {
  const res = await authFetch(`${API_BASE}/user-access/unlock`, {
    method: "POST", headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify({ email }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? `unlockUser: ${res.status}`);
}

// ----- historial de logins (CP-258): Logs Insights async sobre los logs de CoreDB -----

export type LoginEventType =
  | "login_ok" | "login_failed" | "login_locked"
  | "code_sent" | "code_mail_failed" | "code_wait" | "code_locked" | "code_rejected" | "code_unknown_user"
  | "locked" | "locked_mail_failed" | "unlock_link" | "unlocked_admin";

export interface LoginEvent {
  t:        string;           // ISO UTC
  tipo:     LoginEventType;
  ip?:      string;
  client?:  string;
  motivo?:  string;           // login_failed: WrongPassword, NonActiveUser…
  by?:      string;           // unlocked_admin
  intentos?: number;          // unlocked_admin: intentos antes de desbloquear
}

export interface LoginHistory {
  email:     string;
  estado:    "corriendo" | "listo" | "error";
  query_id:  string;
  dias?:     number;
  eventos?:  LoginEvent[];
  resumen?:  Partial<Record<LoginEventType, number>>;
  truncado?: boolean;
  stats?:    { recordsMatched?: number; bytesScanned?: number };
  error?:    string;
}

/** Sin `qid` arranca la búsqueda; con `qid` devuelve el estado / resultado. */
export async function getLoginHistory(email: string, opts: { dias?: number; qid?: string } = {}): Promise<LoginHistory> {
  const qs = new URLSearchParams({ email });
  if (opts.qid) qs.set("qid", opts.qid); else if (opts.dias) qs.set("dias", String(opts.dias));
  const res = await authFetch(`${API_BASE}/user-access/logins?${qs}`, { headers: authHeader() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? `getLoginHistory: ${res.status}`);
  return data as LoginHistory;
}
